import Vue from 'vue'
import Vuex from 'vuex'
import createPersistedState from 'vuex-persistedstate'
import { securedAxiosInstance } from '@/backend/axios'
import { SearchDataModel } from '@/model/searchDataModel'
import _ from 'lodash'
import contracts from './modules/contracts'
import settings from './modules/settings'

Vue.use(Vuex)

export const omitEmptyChildren = (objects) => {
  return objects.map(object => {
    if (_.has(object, 'children') && !_.isEmpty(object.children)) {
      return _.set(object, 'children', omitEmptyChildren(object.children))
    } else {
      return _.omit(object, 'children')
    }
  })
}

const defaultState = {
  currentUser: {},
  membersSearchCondition: {},
  masters: {
    genders: [],
    educationalBackgrounds: [],
    prefectures: [],
    occupations: [],
    changeJobsMotivations: [],
    companyStyles: [],
    skills: [],
    licenses: [],
    specialFavors: [],
    businesses: [],
    employmentStatuses: [],
    evaluations: [],
    languageSkills: [],
    interviewContents: [],
    optionalPermissions: [],
    receivableMailTypes: [],
    autoReplyMailSchedules: [],
    prIconDisplay: [],
    skillExp: [],
    arinashiLabels: [],
    spis: [],
    interviewAcceptableDigitalDocuments: [],
    interviewSelectionPeriods: [],
    interviewSteps: [],
    interviewAvailableHours: [],
    interviewAvailableDays: [],
    interviewDressCodes: [],
    interviewRequiredTimes: [],
    positions: [],
    generations: [],
    interviewFlows: [],
    interviewImportances: [],
    interviewCheckpointCategories: [],
    entryFormTypes: []
  },
  expandsSidebar: true,
  isEnabledDtype: null
}

export const store = new Vuex.Store({
  state: defaultState,
  getters: {
    currentUser (state) {
      return state.currentUser
    },
    showScout (state) {
      return _.get(state.currentUser, 'permissions.isViewScout')
    },
    isSendScout (state) {
      return _.get(state.currentUser, 'permissions.isSendScout')
    },
    showAccounts (state) {
      return _.get(state.currentUser, 'permissions.isViewAccount')
    },
    isEditAccount (state) {
      return _.get(state.currentUser, 'permissions.isEditAccount')
    },
    showEntry (state) {
      return _.get(state.currentUser, 'permissions.isViewEntryUser') || _.get(state.currentUser, 'permissions.isOutsourceeViewEntryUser')
    },
    showAllowedIpAddress (state) {
      return _.get(state.currentUser, 'permissions.isEditAllowedIpAddress')
    },
    showSettingsSelection (state) {
      return _.get(state.currentUser, 'permissions.isEditSettingSelectionPhase')
    },
    canPdfDownload (state) {
      return _.get(state.currentUser, 'permissions.isViewEntryUser')
    },
    canSendMessage (state) {
      return _.get(state.currentUser, 'permissions.isSendMessageEntryUser')
    },
    showDtypeEntryLink (state) {
      return state.isEnabledDtype
    },
    membersSearchCondition: (state) => (jobId) => {
      return _.defaultTo(state.membersSearchCondition[jobId], new SearchDataModel())
    },
    genderMaster (state) {
      return state.masters.genders
    },
    educationalBackgroundMaster (state) {
      return state.masters.educationalBackgrounds
    },
    prefecturesMaster (state) {
      return state.masters.prefectures
    },
    occupationsMaster (state) {
      return state.masters.occupations
    },
    changeJobsMotivationsMaster (state) {
      return state.masters.changeJobsMotivations
    },
    companyStylesMaster (state) {
      return state.masters.companyStyles
    },
    skillsMaster (state) {
      return state.masters.skills
    },
    licensesMaster (state) {
      return state.masters.licenses
    },
    specialFavorsMaster (state) {
      return state.masters.specialFavors
    },
    specialFavorBy: (state) => (id) => {
      return state.masters.specialFavors.find(specialFavor => specialFavor.id === Number(id))
    },
    convertLabelById: (state) => (id, mastersKey) => {
      const target = state.masters[mastersKey].find(m => m.id === Number(id))
      return target ? target.name : ''
    },
    convertLabelForLv2ById: (state) => (id, mastersKey) => {
      const master = state.masters[mastersKey]
      const target = _(master).flatMap('children').find(m => m.id === id)
      return _.defaultTo(target.name, '')
    },
    lowerLayerLabel: (state) => (id, masterKey) => {
      let label = ''
      if (state.masters[masterKey].length) {
        state.masters[masterKey].forEach(category => {
          category.children.forEach(subCategory => {
            subCategory.children.forEach(children => {
              if (children.id === id) {
                label = children.name
              }
            })
          })
        })
      }
      return label
    },
    employmentStatusMaster (state) {
      return state.masters.employmentStatuses
    },
    languageSkillsMaster (state) {
      return state.masters.languageSkills
    },
    businessesMaster (state) {
      return state.masters.businesses
    },
    evaluationMaster (state) {
      return state.masters.evaluations
    },
    interviewContentsMaster (state) {
      return state.masters.interviewContents
    },
    optionalPermissionsMaster (state) {
      return state.masters.optionalPermissions
    },
    receivableMailTypesMaster (state) {
      return state.masters.receivableMailTypes
    },
    autoReplyMailSchedulesMaster (state) {
      return state.masters.autoReplyMailSchedules
    },
    expandsSidebar (state) {
      return state.expandsSidebar
    },
    prIconDisplayMaster (state) {
      return state.masters.prIconDisplay
    },
    skillExpMaster (state) {
      return state.masters.skillExp
    },
    arinashiLabelsMaster (state) {
      return state.masters.arinashiLabels
    },
    spisMaster (state) {
      return state.masters.spis
    },
    interviewAcceptableDigitalDocumentsMaster (state) {
      return state.masters.interviewAcceptableDigitalDocuments
    },
    interviewSelectionPeriodsMaster (state) {
      return state.masters.interviewSelectionPeriods
    },
    interviewStepsMaster (state) {
      return state.masters.interviewSteps
    },
    interviewAvailableHoursMaster (state) {
      return state.masters.interviewAvailableHours
    },
    interviewAvailableDaysMaster (state) {
      return state.masters.interviewAvailableDays
    },
    interviewDressCodesMaster (state) {
      return state.masters.interviewDressCodes
    },
    interviewRequiredTimesMaster (state) {
      return state.masters.interviewRequiredTimes
    },
    positionsMaster (state) {
      return state.masters.positions
    },
    generationsMaster (state) {
      return state.masters.generations
    },
    interviewFlowsMaster (state) {
      return state.masters.interviewFlows
    },
    interviewImportancesMaster (state) {
      return state.masters.interviewImportances
    },
    interviewCheckpointCategoriesMaster (state) {
      return state.masters.interviewCheckpointCategories
    },
    entryFormTypeMaster (state) {
      return state.masters.entryFormTypes
    }
  },
  mutations: {
    setCurrentUser (state, { currentUser }) {
      state.currentUser = currentUser
    },
    setMembersSearchCondition (state, { jobId, membersSearchCondition }) {
      Vue.set(state.membersSearchCondition, jobId, new SearchDataModel(membersSearchCondition))
    },
    deleteMembersSearchConditionByJobId (state, { jobId }) {
      Vue.set(state, 'membersSearchCondition', _.omit(state.membersSearchCondition, [jobId]))
    },
    setMaster (state, { masterKey, data }) {
      Vue.set(state.masters, masterKey, data)
    },
    setExpandsSidebar (state, val) {
      state.expandsSidebar = val
    },
    setIsEnabledDtype (state, isEnabledDtype) {
      state.isEnabledDtype = isEnabledDtype
    },
    clearMaster (state) {
      Vue.set(state, 'masters', {
        genders: [],
        educationalBackgrounds: [],
        prefectures: [],
        occupations: [],
        changeJobsMotivations: [],
        companyStyles: [],
        skills: [],
        licenses: [],
        specialFavors: [],
        businesses: [],
        employmentStatuses: [],
        evaluations: [],
        languageSkills: [],
        interviewContents: [],
        optionalPermissions: [],
        receivableMailTypes: [],
        autoReplyMailSchedules: [],
        prIconDisplay: [],
        skillExp: [],
        arinashiLabels: [],
        spis: [],
        interviewAcceptableDigitalDocuments: [],
        interviewSelectionPeriods: [],
        interviewSteps: [],
        interviewAvailableHours: [],
        interviewAvailableDays: [],
        interviewDressCodes: [],
        interviewRequiredTimes: [],
        positions: [],
        generations: [],
        interviewFlows: [],
        interviewImportances: [],
        interviewCheckpointCategories: [],
        entryFormTypes: []
      })
    },
    clearUser (state) {
      Vue.set(state, 'currentUser', {})
    },
    clearSearchCondition (state) {
      Vue.set(state, 'membersSearchCondition', {})
    },
    clearIsEnabledDtype (state) {
      Vue.set(state, 'isEnabledDtype', null)
    },
    clearVuex (state) {
      Object.assign(state, defaultState)
    }
  },
  actions: {
    storeMaster ({ commit, state }, masterKey) {
      if (state.masters[masterKey].length === 0) {
        return securedAxiosInstance.get(`/masters/${_.snakeCase(masterKey)}`)
          .then(response => {
            commit('setMaster', { masterKey: masterKey, data: omitEmptyChildren(response.data) })
          })
      }
    },
    setMembersSearchCondition ({ commit }, { jobId, membersSearchCondition }) {
      commit('setMembersSearchCondition', { jobId, membersSearchCondition })
    },
    deleteMembersSearchConditionByJobId ({ commit }, { jobId }) {
      commit('deleteMembersSearchConditionByJobId', { jobId })
    },
    setCurrentUser ({ commit }, { currentUser }) {
      commit('setCurrentUser', { currentUser: currentUser })
    },
    setIsEnabledDtype ({ commit, state }) {
      if (state.isEnabledDtype === null) {
        return securedAxiosInstance.get('/dtype/company/contracts/is_enabled')
          .then(response => {
            commit('setIsEnabledDtype', response.data)
          })
      }
    },
    toggleExpandsSidebar ({ commit, state }) {
      commit('setExpandsSidebar', !state.expandsSidebar)
    },
    clearMaster ({ commit }) {
      commit('clearMaster')
    },
    clearUser ({ commit }) {
      commit('clearUser')
    },
    clearSearchCondition ({ commit }) {
      commit('clearSearchCondition')
    },
    clearIsEnabledDtype ({ commit }) {
      commit('clearIsEnabledDtype')
    },
    clearVuex ({ commit }) {
      commit('clearVuex')
    }
  },
  modules: {
    contracts,
    settings
  },
  plugins: [createPersistedState({ storage: window.sessionStorage })]
})
